import { Directory, Filesystem } from "@capacitor/filesystem";
import { focusToBackgroundStyle, IImageFocus, spErrorTags } from "@eventr/common";
import { ColoursObj, useToken } from "@eventr/common-storybook";
import { BoxProps, LinearProgress } from "@mui/material";
import axios, { AxiosRequestConfig } from "axios";
import { motion } from "framer-motion";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

interface ISlideImageProps extends BoxProps {
  src: string;
  addLoading?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  noGradient?: boolean;
  /** Optional framing for `src`. Absent renders exactly as it does today. */
  focus?: IImageFocus;
  /** Told the picture once it is painted, with the box it covers. */
  onPainted?: (url: string, frame: { width: number; height: number }) => void;
}

/** Pictures loaded and decoded this session, by source URL, as URLs ready to paint. */
const loadedPictures = new Map<string, string>();
const pendingPictures = new Map<string, Promise<string>>();

/**
 * The picture's bytes in base64: from the file cache, or fetched with the user's token and
 * written to it. `onFetch` is called when it has to be fetched.
 */
async function readPicture(src: string, onFetch: () => void): Promise<{ base64: string; type: string }> {
  // The cache path is the URL without its domain.
  const filePath = src.replace(/^(?:\/\/|[^/]+)*\//, "");
  try {
    const cached = await Filesystem.readFile({ path: filePath, directory: Directory.Cache });
    if (typeof cached.data === "string" && cached.data) return { base64: cached.data, type: "" };
  } catch {
    // Not in the file cache.
  }

  onFetch();
  const response = await axios.get<ArrayBuffer>(src, {
    responseType: "arraybuffer",
    headers: { Authorization: `Bearer ${useToken.getState().getToken()}` },
  } as AxiosRequestConfig);
  const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ""));
  Filesystem.writeFile({ path: filePath, data: base64, directory: Directory.Cache, recursive: true });
  return { base64, type: String(response.headers["content-type"] ?? "") };
}

/**
 * The picture at `src` as a URL to paint, decoded so that it paints in one go. Each iOS native
 * view is its own document, which cannot open another's blob URL, so there it is a data URL.
 */
function loadPicture(src: string, onFetch: () => void): Promise<string> {
  const pending =
    pendingPictures.get(src) ??
    readPicture(src, onFetch).then(async ({ base64, type }) => {
      const url = isIosNativeNavigation()
        ? `data:${type || "image/png"};base64,${base64}`
        : URL.createObjectURL(new Blob([Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))], { type }));
      const image = new Image();
      image.src = url;
      await image.decode().catch(() => undefined);
      loadedPictures.set(src, url);
      return url;
    });
  pendingPictures.set(src, pending);
  pending.catch(() => undefined).finally(() => pendingPictures.delete(src));
  return pending;
}

/**
 * True when `src` can be handed straight to CSS/`<img>` rather than fetched with the user's
 * bearer token: an inline blob/data url, or an app-relative asset from our own bundle.
 */
const isDirectSrc = (src: string) =>
  src.startsWith("blob:") || src.startsWith("data:") || src.startsWith("/") || src.startsWith("./");

export const SlideImage = (props: ISlideImageProps) => {
  const [isLoading, setIsLoading] = useState(false);
  // we need make image request here using axios so that auth token is sent
  // and set the image src to the response

  const linearGradient = `linear-gradient(0deg, ${ColoursObj.backgrounds.header}D9,  ${ColoursObj.backgrounds.header}10)`;
  const { src, component, onError, focus } = props;

  // `focusToBackgroundStyle(undefined)` is cover / 50% 50% / no-repeat - exactly what every
  // caller passes today, so spreading it over `style` is a no-op until a focus exists.
  const framing = focusToBackgroundStyle(focus);

  // A zoom is a `scale()`, and this element also holds the caller's overlay content. Scaling
  // that with the picture is not what a focal point means, so above 1x the painted layer is
  // split out beneath the children. At 1x there is no transform and this branch never runs,
  // which is what keeps the unfocused case byte-identical.
  const isZoomed = !!framing.transform && component !== "img";

  const ref = useRef<HTMLDivElement | HTMLImageElement>(null);
  // Held in a ref so a new callback each render does not fetch the picture again.
  const onPaintedRef = useRef(props.onPainted);
  useEffect(() => {
    onPaintedRef.current = props.onPainted;
  });
  // A layout effect, so a picture already loaded is painted before the browser draws.
  useLayoutEffect(() => {
    const paint = (url: string) => {
      if (!ref.current) return;
      ref.current.style.backgroundImage = `${linearGradient}, url(${url})`;
      // offset sizes, not the bounding box: a zoomed picture is scaled, its frame is not.
      const { offsetWidth: width, offsetHeight: height } = ref.current;
      onPaintedRef.current?.(url, { width, height });
    };

    if (!ref) return;

    // Only a fetch shows the bar. A run cancelled mid-fetch leaves it set, so every path that
    // does not fetch clears it.
    if (!src || isDirectSrc(src) || loadedPictures.has(src)) setIsLoading(false);

    if (!src) {
      if (component === "img") {
        if (ref.current instanceof HTMLImageElement) ref.current.src = src;
      }

      if (component !== "img") {
        if (ref.current && ref.current.style) {
          ref.current.style.backgroundImage = "";
        }
      }
      return;
    }
    // A blob/data url is already the picture, and a bundled asset ("/assets/foo.png",
    // "/src/... " in dev) is served by the app itself: it carries no auth and the platform
    // already caches it. Pushing those through the authenticated fetch below re-downloaded
    // and re-wrote the bundled fallback splash to the filesystem cache on every mount.
    if (isDirectSrc(src)) {
      if (component === "img") {
        if (ref.current instanceof HTMLImageElement) ref.current.src = src;
      }

      if (component !== "img") {
        paint(src);
      }
      return;
    }

    // A picture this session has already shown paints before the first frame.
    const loaded = loadedPictures.get(src);
    if (loaded) {
      paint(loaded);
      return;
    }

    let cancelled = false;
    loadPicture(src, () => !cancelled && setIsLoading(true))
      .then((url) => !cancelled && paint(url))
      .catch((err) => {
        spErrorTags("Error Fetching image", ["AuthImage"], err);
        if (!cancelled) onError?.();
      })
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [component, isZoomed, linearGradient, onError, src]);

  // if loading add
  const { addLoading, children, style } = props;

  return (
    <div style={{ position: "relative", height: "100%" }}>
      {isZoomed ? (
        <motion.div style={{ ...style, position: style?.position ?? "relative", overflow: "hidden" }}>
          <div ref={ref as RefObject<HTMLDivElement>} style={{ position: "absolute", inset: 0, ...framing }} />
          {/* Above the painted layer, and deliberately outside the scale. */}
          <div style={{ position: "relative", height: "100%" }}>{children}</div>
        </motion.div>
      ) : (
        <motion.div ref={ref} style={{ ...style, ...framing }}>
          {children}
        </motion.div>
      )}

      {addLoading && isLoading && (
        <LinearProgress
          variant="query"
          color="secondary"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 500,
          }}
        />
      )}
    </div>
  );
};
